import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../../lib/api";
import { rememberCompaniesSearch } from "../../lib/companies-back";
import { crmApi, type CompanyListParams } from "../../lib/crm-api";
import { useIsDesktop } from "../../lib/media";
import { queryKeys } from "../../lib/queryKeys";
import { relativeTime } from "../../lib/time";
import type {
  CompanyRow,
  CompanyWrite,
  CrmListView,
  FilterOption,
  PicklistOption,
} from "../../lib/crm-types";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import {
  columnAlignment,
  columnVisibility,
  renderColumn,
} from "../../components/crm/companyColumns";
import {
  clampWidth,
  MAX_WIDTH,
  MIN_WIDTH,
  useColumnWidths,
} from "../../components/crm/useColumnWidths";
import { optionLabel } from "../../components/crm/FieldPickers";
import { Popover } from "../../components/ui/Popover";
import { moved, useReorder } from "../../components/ui/useReorder";

/**
 * Every account, filterable.
 *
 * A table rather than cards. This is read by somebody comparing thirty rows
 * down a column -- who owns what, which stage they are at -- and cards make
 * that comparison impossible while using four times the vertical space.
 *
 * Filters live in the URL so a slice is bookmarkable and pasteable: "have a
 * look at the unowned installers" is a link, not a list of instructions.
 *
 * On a phone it is a list after all, and the argument above is why. Nobody
 * compares thirty rows down a column on a screen two columns wide: the table
 * there was the name and one other heading, with every column the desk had
 * chosen hidden behind a breakpoint and the filter bar wrapped into a staircase
 * of selects above it. A phone is for finding *the* company and opening it, so
 * it gets a row per company -- the name, Team, and one line of who has it and
 * where it stands -- and the filters fold behind one button. Same URL, same
 * query; only the drawing differs.
 */
const PAGE_SIZE = 50;

export function CompaniesList() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  /**
   * The ad-hoc filters, read back out of the URL.
   *
   * Everything the filter bar sets is `f_<key>`, so the bar can carry a field
   * this component knows nothing about -- including a custom one, as `f_cf:3`.
   * The four named parameters below it are the older spelling and are still
   * read, so links people have already saved keep working.
   */
  const adhoc = Object.fromEntries(
    [...params.entries()]
      .filter(([key]) => key.startsWith("f_"))
      .map(([key, value]) => [key.slice(2), value]),
  );

  /*
   * Who is looking, and what they asked the list to open on.
   *
   * Applied only to a bare `/crm/companies` -- the sidebar link, a fresh
   * visit -- and never over a URL that says something, so a pasted or
   * bookmarked slice is always what it says. Choosing All on the list is
   * written as `owner=all` rather than as nothing, which is what stops the
   * preference putting "Mine" straight back.
   */
  const mySettings = useQuery({
    queryKey: queryKeys.mySettings,
    queryFn: api.mySettings,
    staleTime: 5 * 60_000,
  });
  const myId = mySettings.data?.id ?? null;
  const bare = [...params.keys()].length === 0;
  const opensOnMine = mySettings.data?.crmCompaniesScope === "mine";

  useEffect(() => {
    if (bare && opensOnMine && myId !== null) {
      setParams({ owner: String(myId) }, { replace: true });
    }
  }, [bare, opensOnMine, myId, setParams]);

  /* Remembered for the company page's way back -- see `companies-back.ts`. */
  useEffect(() => rememberCompaniesSearch(params.toString()), [params]);

  const ownerParam = params.get("owner");
  const mine = myId !== null && ownerParam === String(myId);

  const query: CompanyListParams = {
    q: params.get("q") ?? undefined,
    ownerUserId: ownerParam && /^\d+$/.test(ownerParam) ? Number(ownerParam) : undefined,
    unowned: params.get("unowned") === "true",
    lifecycleStage: params.get("lifecycle") ?? undefined,
    customerType: params.get("type") ?? undefined,
    leadPriority: params.get("priority") ?? undefined,
    /*
     * The branch directory. A group's page links here rather than rendering its
     * own list, so nine hundred branches get the search, paging and columns
     * this view already has instead of a worse copy of them.
     */
    parentCompanyId: params.get("parent") ? Number(params.get("parent")) : undefined,
    sort: params.get("sort") ?? "name",
    dir: (params.get("dir") as "asc" | "desc" | null) ?? undefined,
    filters: adhoc,
    limit: PAGE_SIZE,
    offset: Number(params.get("offset") ?? 0),
  };

  const sortKey = query.sort ?? "name";
  const sortDir = query.dir ?? "asc";

  /**
   * Click a heading to sort by it; click it again to reverse.
   *
   * The direction resets to ascending when the column changes rather than
   * carrying over. Carrying it means clicking "Last activity" after having
   * sorted Name Z-A silently gives newest-first, which is not what the click
   * asked for and is invisible until somebody reads the arrow.
   */
  const sortBy = (key: string): void => {
    const next = new URLSearchParams(params);
    next.delete("offset");
    if (key === sortKey) {
      next.set("dir", sortDir === "asc" ? "desc" : "asc");
    } else {
      next.set("sort", key);
      next.set("dir", "asc");
    }
    setParams(next, { replace: true });
  };

  const filters = useQuery({
    queryKey: queryKeys.crmFilters,
    queryFn: crmApi.filters,
    staleTime: 5 * 60_000,
  });

  /*
   * This person's filter bar. Per user, unlike the columns below -- a bar is
   * the set of tools for the job somebody is doing today.
   */
  const bar = useQuery({
    queryKey: queryKeys.crmFilterBar("companies"),
    queryFn: () => crmApi.filterBar("companies"),
    staleTime: 5 * 60_000,
  });

  const saveBar = useMutation({
    mutationFn: (keys: string[]) => crmApi.saveFilterBar(keys, "companies"),
    onSuccess: (next) =>
      /* Written straight into the cache rather than invalidated: the response
         is the new state, and a refetch would blank the bar for a frame. */
      queryClient.setQueryData(queryKeys.crmFilterBar("companies"), next),
  });

  const resetBar = useMutation({
    mutationFn: () => crmApi.resetFilterBar("companies"),
    onSuccess: (next) =>
      queryClient.setQueryData(queryKeys.crmFilterBar("companies"), next),
  });

  /*
   * The shared column layout. Everyone's list, set by a manager -- see
   * `views.controller.ts` for why it is not a per-agent preference.
   */
  const view = useQuery({
    queryKey: queryKeys.crmListView("companies"),
    queryFn: () => crmApi.listView("companies"),
    /*
     * Half a minute, not five. A manager changing the layout invalidates their
     * own copy, but everybody else's browser only finds out when this goes
     * stale -- and a colleague told "I've added Lead source" who then stares
     * at a list without it for five minutes concludes the setting is broken.
     * It is one small row from a table with a single row in it.
     */
    staleTime: 30_000,
  });

  const companies = useQuery({
    queryKey: queryKeys.crmCompanies(query as Record<string, unknown>),
    queryFn: () => crmApi.companies(query),
    staleTime: 15_000,
    /* Not the whole desk for a frame before "Mine" lands. */
    enabled: !(bare && (mySettings.isLoading || opensOnMine)),
  });

  const set = (key: string, value: string | null): void => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    /*
     * Any *filter* change invalidates the page position -- but paging is a
     * change to the position itself, and clearing it here is what made Next do
     * nothing at all: it set offset=50 and deleted it again on the next line.
     */
    if (key !== "offset") next.delete("offset");
    setParams(next, { replace: true });
  };

  /** Which row is saving, so only its cell dims rather than the whole table. */
  const [savingId, setSavingId] = useState<number | null>(null);

  /* How wide each column is on this screen. Per browser, not per desk -- see
     `useColumnWidths`. */
  const widths = useColumnWidths("companies");

  /**
   * This person's columns: which ones, and in what order.
   *
   * Two callers, one request. Dragging a heading reorders them and the Columns
   * menu adds and removes them, and both are the same PUT to the same layer --
   * a second mutation for the second control would have been two ways to write
   * one row, optimistically, racing each other.
   *
   * Written to this person's own layer -- the same thing the settings panel's
   * "Mine" tab writes. Which columns you read and in what order is a decision
   * about how you work, and neither a drag on a table header nor a tick in a
   * menu is a moment anybody expects to be changing what the rest of the desk
   * sees; rewriting the team's or the desk's layout from here would be exactly
   * that.
   *
   * It has one consequence worth knowing, and the settings panel is where it
   * is spelled out: a personal layer stops inheriting. A column a manager
   * later adds to the team's layout will not appear here until this person
   * presses "Follow the desk's again", which is in the same panel.
   *
   * Width is not saved with it. Widths are per screen and live in
   * localStorage; order is per person and lives on the server, because one
   * describes the monitor you are sitting at and the other describes you.
   */
  const saveColumns = useMutation({
    mutationFn: (next: string[]) => crmApi.saveMyListView(next),
    /*
     * Moved on screen before the request is answered. A drag that visibly
     * snaps back for half a second reads as having failed, and the person
     * does it again.
     */
    onMutate: async (next: string[]) => {
      const key = queryKeys.crmListView("companies");
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CrmListView>(key);
      if (previous) {
        queryClient.setQueryData<CrmListView>(key, { ...previous, columns: next });
      }
      return { previous };
    },
    onError: (_error, _next, context) => {
      /* Put it back where it was. Leaving the optimistic order up would mean
         a layout that survives until the next refetch and then jumps. */
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.crmListView("companies"), context.previous);
      }
    },
    onSuccess: (view) => {
      queryClient.setQueryData(queryKeys.crmListView("companies"), view);
    },
  });

  /**
   * Give up this person's own columns and follow the team's again.
   *
   * The counterpart nobody thinks to build: a personal layer stops inheriting,
   * so a column a manager adds to the team's layout never arrives, and without
   * this the only way back was a settings page most people cannot open.
   */
  const resetColumns = useMutation({
    mutationFn: () => crmApi.resetMyListView(),
    onSuccess: (view) => {
      queryClient.setQueryData(queryKeys.crmListView("companies"), view);
    },
  });

  /**
   * Change one field on one row, without leaving the list.
   *
   * The list is where somebody works a queue -- thirty unmanaged accounts, or
   * a column of blank stages -- and opening each company to change one field
   * and coming back is most of the work. The same pickers the company page
   * uses, so the two cannot drift.
   */
  const patch = useMutation({
    mutationFn: ({
      row,
      patch,
    }: {
      row: CompanyRow;
      patch: Partial<CompanyWrite>;
    }) => {
      setSavingId(row.id);
      return crmApi.updateCompany(row.id, patch);
    },
    onSettled: () => setSavingId(null),
    onSuccess: (_result, { row }) => {
      /*
       * The list, because a changed value can move a row out of the current
       * filter, and the company, because its page may be cached behind this
       * one and would otherwise still show the old owner.
       */
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompaniesRoot });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(row.id),
      });
    },
  });

  /**
   * The same change, for a manager-defined field.
   *
   * Its own endpoint and so its own mutation -- custom values are rows in their
   * own table, not columns on the company.
   */
  const patchCustom = useMutation({
    mutationFn: ({
      row,
      fieldId,
      value,
    }: {
      row: CompanyRow;
      fieldId: number;
      value: string | null;
    }) => {
      setSavingId(row.id);
      return crmApi.setCustomField(row.id, fieldId, value);
    },
    onSettled: () => setSavingId(null),
    onSuccess: (_result, { row }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompaniesRoot });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(row.id),
      });
    },
  });

  /* Needed to know which `cf:` columns are dropdowns, and what is in them. */
  const customFields = useQuery({
    queryKey: queryKeys.crmFields,
    queryFn: crmApi.fields,
    staleTime: 5 * 60_000,
  });

  const picklists = filters.data?.picklists ?? {};

  /**
   * The bar, resolved to the fields it names.
   *
   * Filtered against `available`, so a filter on a field a manager has since
   * hidden or deleted simply drops out rather than rendering a control with no
   * options and no label.
   */
  const shownFilters = (bar.data?.filters ?? [])
    .map((key) => bar.data?.available.find((option) => option.key === key))
    .filter((option): option is NonNullable<typeof option> => option !== undefined);
  const rows = companies.data?.companies ?? [];
  const columns = view.data?.columns ?? [];

  /*
   * Headings run across the page, so the drag is measured on x.
   *
   * The threshold is what lets one heading be both a sort button and a drag
   * handle: below it the pointer never takes hold and the click sorts as it
   * always did, above it the column comes with you. A separate grip would have
   * been simpler and worse -- a five-pixel target on a heading that is already
   * only twelve pixels tall.
   */
  const reorder = useReorder(
    (from, to) => saveColumns.mutate(moved(columns, from, to)),
    { axis: "x", threshold: 5 },
  );

  const labelFor = (key: string): string =>
    view.data?.available.find((column) => column.key === key)?.label ?? key;
  const sortableColumns = new Set(
    (view.data?.available ?? [])
      .filter((column) => column.sortable)
      .map((column) => column.key),
  );
  /* Named from the rows themselves, so no extra request just for a heading. */
  const groupName = query.parentCompanyId ? rows[0]?.parentName : null;
  const total = companies.data?.total ?? 0;
  const offset = query.offset ?? 0;

  const isDesktop = useIsDesktop();
  /*
   * Whether the filters are showing, on a phone. Closed to begin with: the bar
   * is four or five selects at the size iOS insists on, which was a screen's
   * worth of controls above the first company. The count on the button is what
   * keeps a closed bar honest -- a narrowed list with nothing on screen to say
   * so reads as missing records.
   */
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters = shownFilters.filter((option) =>
    params.get(`f_${option.key}`),
  ).length;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <h1 className="text-sm font-semibold text-content">
          {query.parentCompanyId ? (groupName ?? "Branches") : "Companies"}
        </h1>
        <span className="text-xs text-faint">{total.toLocaleString()}</span>

        {query.parentCompanyId ? (
          <>
            <Link
              to={`/crm/companies/${query.parentCompanyId}`}
              className="text-xs text-brand-600 hover:text-brand-700"
            >
              Open the group
            </Link>
            <button
              type="button"
              onClick={() => set("parent", null)}
              className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
            >
              Show all companies
            </button>
          </>
        ) : null}

        {/* Everybody's or yours. Not in the filter bar, because it is not
            one of the tools somebody adds for a job -- it is which list this
            is, and it opens on the person's own choice from Your settings. */}
        {myId !== null && !query.parentCompanyId ? (
          <div className="inline-flex rounded-md border border-line p-0.5">
            {(
              [
                ["all", "All", !mine],
                ["mine", "Mine", mine],
              ] as const
            ).map(([key, label, on]) => (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => set("owner", key === "mine" ? String(myId) : "all")}
                className={cn(
                  "h-7 rounded px-2.5 text-xs",
                  on ? "bg-brand-600 font-medium text-white" : "text-muted hover:text-content",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((was) => !was)}
          className={cn(
            "ml-auto flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs md:hidden",
            filtersOpen || activeFilters > 0
              ? "border-brand-500 text-brand-600 dark:text-brand-400"
              : "border-line text-muted",
          )}
        >
          Filters
          {activeFilters > 0 ? (
            <span className="rounded-full bg-brand-600 px-1.5 text-[10px] font-medium leading-4 text-white">
              {activeFilters}
            </span>
          ) : null}
        </button>

        {/* The whole width on a phone, on its own line under the title: it is
            the control used most there, and a 48-wide box squeezed beside All
            and Mine held about six letters. */}
        <input
          value={params.get("q") ?? ""}
          onChange={(event) => set("q", event.target.value)}
          placeholder="Filter by name…"
          aria-label="Filter companies by name"
          className="h-8 w-full rounded-md border border-line bg-surface px-2 text-xs text-content outline-none focus:border-brand-400 md:ml-2 md:w-48"
        />

        {/*
          The bar this person has built, not four fixed dropdowns. See
          `crm_filter_prefs` for why it is per user where the columns are not.

          `contents` on a desktop, so the wrapper is not there at all and the
          controls flow along the header as they always have. On a phone it is
          the panel the Filters button opens: one filter to a line, each the
          full width, so a long picklist label is read rather than clipped.
        */}
        <div
          className={cn(
            filtersOpen ? "flex w-full flex-col gap-2" : "hidden",
            "md:contents",
          )}
        >
          {isDesktop ? null : (
            <PhoneSort
              sortKey={sortKey}
              sortDir={sortDir}
              columns={(view.data?.available ?? []).filter((column) => column.sortable)}
              onChange={(key, dir) => {
                const next = new URLSearchParams(params);
                next.delete("offset");
                next.set("sort", key);
                next.set("dir", dir);
                setParams(next, { replace: true });
              }}
            />
          )}
          {shownFilters.map((option) => (
            <FilterControl
              key={option.key}
              option={option}
              value={params.get(`f_${option.key}`) ?? ""}
              owners={filters.data?.owners ?? []}
              picklists={picklists}
              onChange={(value) => set(`f_${option.key}`, value)}
              onRemove={() => {
                /* Clear the value as it goes. A filter removed from the bar but
                   left in the URL would keep narrowing the list with nothing on
                   screen to say so -- which reads as missing records. */
                const next = new URLSearchParams(params);
                next.delete(`f_${option.key}`);
                next.delete("offset");
                setParams(next, { replace: true });
                saveBar.mutate(
                  shownFilters
                    .filter((entry) => entry.key !== option.key)
                    .map((entry) => entry.key),
                );
              }}
            />
          ))}

          <AddFilter
            available={(bar.data?.available ?? []).filter(
              (option) => !shownFilters.some((entry) => entry.key === option.key),
            )}
            onAdd={(key) => saveBar.mutate([...shownFilters.map((e) => e.key), key])}
            onReset={bar.data?.isDefault ? undefined : () => resetBar.mutate()}
          />
        </div>

        {/* Beside "+ Filter" and not in Settings, which is where this used to
            be and where most of the desk cannot go: /settings is admins only,
            so a rep who wanted a custom field on screen had to ask for it.
            Not on a phone, whose rows draw no columns to choose. */}
        {isDesktop ? (
          <EditColumns
            available={view.data?.available ?? []}
            columns={columns}
            onChange={(next) => saveColumns.mutate(next)}
            onFollowTeam={
              view.data?.layers.personal != null
                ? () => resetColumns.mutate()
                : undefined
            }
            followLabel={
              view.data?.team ? `${view.data.team.name}'s` : "the desk's"
            }
          />
        ) : null}

        {/* Beside the filters rather than over the row that failed: a popover
            has already shut by the time the save comes back, and a message
            inside a table cell would shift the column it is in. */}
        {patch.isError || patchCustom.isError ? (
          <p className="w-full text-xs text-sla-breached">
            {(() => {
              const cause = patch.error ?? patchCustom.error;
              return cause instanceof ApiError
                ? cause.message
                : "Could not save that change.";
            })()}
          </p>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-auto">
        {companies.isLoading ? (
          <p className="p-8 text-center text-xs text-faint">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 p-8 text-center">
            <p className="text-sm font-medium text-muted">No companies match</p>
            <p className="max-w-xs text-xs text-faint">
              Clear a filter, or add the account by hand. Companies also appear
              on their own as soon as somebody from a new business emails in.
            </p>
          </div>
        ) : !isDesktop ? (
          <ul>
            {rows.map((row) => (
              <PhoneRow key={row.id} row={row} picklists={picklists} />
            ))}
          </ul>
        ) : (
          /*
           * Fixed layout, so a column is the width it is set to. Left to itself
           * the browser shares the spare width between the columns in rough
           * proportion to their contents, which is how a one-letter credit
           * rating ended up wider than a city -- and it also means a heading
           * cannot be dragged anywhere, since nothing the person sets survives
           * the next re-measure.
           */
          <table className="w-full table-fixed border-collapse text-sm">
            {/*
              The background goes on the cells, not on this row group.

              `border-collapse: collapse` hands painting to the cells, and
              WebKit does not paint a background set on `thead` at all -- so
              the sticky header was transparent on an iPhone and the rows slid
              up through the column names as you scrolled. Chrome paints it,
              which is why it looked right everywhere it was tested. `Th`
              carries `bg-app` for that reason and it is not decoration.
            */}
            <thead className="sticky top-0 z-10 text-left text-[11px] uppercase tracking-wide text-faint">
              <tr>
                {/*
                  Always first and never configurable: every row has to be
                  clickable through to the company.

                  The one column with no width of its own, so it takes whatever
                  the others leave. Something has to absorb the spare space, and
                  a name is both the widest value here and the one worth reading
                  in full.
                */}
                <Th>
                  <SortButton
                    label="Company"
                    active={sortKey === "name"}
                    dir={sortDir}
                    onClick={() => sortBy("name")}
                  />
                </Th>
                {columns.map((key, index) => (
                  <Th
                    key={key}
                    width={widths.widthOf(key)}
                    onResize={(px) => widths.setWidth(key, px)}
                    onResetWidth={() => widths.resetWidth(key)}
                    drag={{ ...reorder.row(index), ...reorder.handle(index) }}
                    className={cn(
                      columnVisibility(index),
                      columnAlignment(key),
                      /* Not `grab` at rest. A hand on every heading would
                         promise a drag over the resize handle and the sort
                         button too, which are the two things most of these
                         clicks actually are. */
                      reorder.dragging !== null && "select-none",
                      reorder.dragging === index && "opacity-40",
                    )}
                    dropSide={dropEdgeFor(reorder, index)}
                  >
                    {/* Sortability comes from the server with the column, so
                        the client keeps no second list of which headings can
                        be clicked -- the sort of duplicated knowledge that
                        goes quietly wrong when a field is added. */}
                    {sortableColumns.has(key) ? (
                      <SortButton
                        label={labelFor(key)}
                        active={sortKey === key}
                        dir={sortDir}
                        onClick={() => sortBy(key)}
                      />
                    ) : (
                      labelFor(key)
                    )}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                /*
                 * Banded rather than ruled. Thirty rows read across five
                 * columns is where the eye loses its place, and a filled band
                 * carries it further than a hairline does -- the stripe is the
                 * separator, so the per-row border would only add noise on top
                 * of it. Hover stays a third shade so it is still visible on
                 * both bands.
                 */
                <tr
                  key={row.id}
                  className="odd:bg-surface even:bg-app hover:bg-surface-2"
                >
                  <td className="px-3 py-2">
                    {/* Team beside the name rather than in a column of its
                        own: going from the list to the account in Team is a
                        thing people do all day, and a configurable column is
                        one somebody can hide. */}
                    <div className="flex min-w-0 items-baseline gap-2">
                      <Link
                        to={`/crm/companies/${row.id}`}
                        className="min-w-0 truncate font-medium text-content hover:text-brand-600"
                      >
                        {row.name}
                      </Link>
                      {row.externalUrl ? (
                        <a
                          href={row.externalUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open this account in Team"
                          className="shrink-0 rounded border border-line px-1.5 text-[11px] font-medium text-muted hover:border-brand-500 hover:text-brand-600 dark:hover:text-brand-400"
                        >
                          Team ↗
                        </a>
                      ) : null}
                    </div>
                    {/*
                      The second line is always present, even when there is
                      nothing to put on it. An empty div is nought pixels tall,
                      so a company with no domain sat in a row shorter than the
                      ones either side of it, and a column of rows that are
                      almost but not quite the same height is harder to read
                      across than one of any single height. The dash is the same
                      "we do not have this" the cells use.
                    */}
                    <div className="flex items-center gap-1.5 text-[11px] text-faint">
                      {row.domain ? (
                        <span className="truncate">{row.domain}</span>
                      ) : row.parentName ? null : (
                        <span>—</span>
                      )}
                      {/* A branch is meaningless without its group. */}
                      {row.parentName ? (
                        <Link
                          to={`/crm/companies/${row.parentCompanyId}`}
                          className="truncate hover:text-brand-600"
                        >
                          part of {row.parentName}
                        </Link>
                      ) : null}
                      {row.isAutoCreated ? (
                        <span
                          title="Created from an email domain. Nobody has checked it yet."
                          className="shrink-0 rounded bg-surface-2 px-1 text-[9px] uppercase text-faint"
                        >
                          unchecked
                        </span>
                      ) : null}
                    </div>
                  </td>

                  {columns.map((key, index) => (
                    <td
                      key={key}
                      className={cn(
                        /* Clipped rather than wrapped: a value too long for the
                           width somebody chose must not be the one row in fifty
                           that is two lines tall. Drag the column wider, or open
                           the company. */
                        "truncate px-3 py-2 text-xs text-muted",
                        columnVisibility(index),
                        columnAlignment(key),
                      )}
                    >
                      {renderColumn(key, row, {
                        picklists,
                        pendingId: savingId,
                        customFields: customFields.data,
                        onPatch: (target, changes) =>
                          patch.mutate({ row: target, patch: changes }),
                        onCustomPatch: (target, fieldId, value) =>
                          patchCustom.mutate({ row: target, fieldId, value }),
                      })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > PAGE_SIZE ? (
        <footer className="flex items-center gap-3 border-t border-line px-4 py-2 text-xs text-muted">
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => set("offset", String(Math.max(offset - PAGE_SIZE, 0)))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => set("offset", String(offset + PAGE_SIZE))}
          >
            Next
          </Button>
        </footer>
      ) : null}
    </div>
  );
}

/**
 * A clickable heading, with the arrow only on the column actually sorting.
 *
 * The arrow marks one column rather than every sortable one. A row of headings
 * each carrying a faint arrow says nothing about which is in force, which is
 * the single thing the indicator exists to say.
 */
function SortButton({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      /* So a screen reader announces the state rather than only the arrow. */
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      className={cn(
        "inline-flex items-center gap-1 uppercase tracking-wide hover:text-brand-600 dark:hover:text-brand-400",
        active && "text-content",
      )}
    >
      {label}
      {active ? (
        <span aria-hidden className="text-[9px]">
          {dir === "asc" ? "▲" : "▼"}
        </span>
      ) : null}
    </button>
  );
}

/**
 * One company, on a phone.
 *
 * The name, Team beside it, and one line under it: the group or the domain,
 * who manages it, its stage and when anybody last touched it. Those are what
 * tell two similar names apart and say whether this is the account somebody is
 * looking for -- the rest is one tap away on the company itself.
 *
 * Read-only, unlike the table. The table's pickers are for working a queue in
 * place, and a picker opened by a thumb scrolling past is a changed owner
 * nobody meant.
 *
 * Team sits outside the row's link rather than in it -- a link inside a link
 * is invalid, and a browser that splits it opens the company when Team was
 * pressed.
 */
function PhoneRow({
  row,
  picklists,
}: {
  row: CompanyRow;
  picklists: Record<string, PicklistOption[]>;
}) {
  const stage = row.lifecycleStage
    ? optionLabel(picklists.lifecycle_stage ?? [], row.lifecycleStage)
    : null;
  const where = row.parentName ? `part of ${row.parentName}` : row.domain;

  return (
    <li className="flex items-center gap-2 border-b border-line bg-surface pr-3">
      <Link
        to={`/crm/companies/${row.id}`}
        className="min-w-0 flex-1 py-2.5 pl-4 active:bg-surface-2"
      >
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 truncate text-sm font-medium text-content">
            {row.name}
          </span>
          {row.isAutoCreated ? (
            <span className="shrink-0 rounded bg-surface-2 px-1 text-[9px] uppercase text-faint">
              unchecked
            </span>
          ) : null}
        </span>
        <span className="mt-0.5 flex items-center gap-2 text-[11px] text-faint">
          <span className="min-w-0 flex-1 truncate">
            {where ? `${where} · ` : null}
            <span className={row.ownerName ? undefined : "text-sla-warning"}>
              {row.ownerName ?? "Unmanaged"}
            </span>
            {stage ? ` · ${stage}` : null}
          </span>
          {row.lastActivityAt ? (
            <span className="shrink-0 tabular-nums">
              {relativeTime(row.lastActivityAt)}
            </span>
          ) : null}
        </span>
      </Link>
      {row.externalUrl ? (
        <a
          href={row.externalUrl}
          target="_blank"
          rel="noreferrer"
          title="Open this account in Team"
          className="flex h-8 shrink-0 items-center rounded-md border border-line px-2 text-[11px] font-medium text-muted active:bg-surface-2"
        >
          Team ↗
        </a>
      ) : null}
    </li>
  );
}

/**
 * The order of the list, on a phone, where there are no headings to press.
 *
 * Every sortable column in both directions, as one select. Two controls --
 * a field and an arrow -- would be the faithful copy of the table, and twice
 * the things to find on a screen this size.
 */
function PhoneSort({
  sortKey,
  sortDir,
  columns,
  onChange,
}: {
  sortKey: string;
  sortDir: "asc" | "desc";
  columns: { key: string; label: string }[];
  onChange: (key: string, dir: "asc" | "desc") => void;
}) {
  const choices = [
    { key: "name", label: "Name" },
    ...columns.filter((column) => column.key !== "name"),
  ];

  return (
    <label className="flex items-center gap-1 text-[11px] font-medium text-faint">
      <span className="w-12 shrink-0 text-center">Sort</span>
      <select
        value={`${sortKey}:${sortDir}`}
        onChange={(event) => {
          const [key, dir] = event.target.value.split(":");
          onChange(key!, dir === "desc" ? "desc" : "asc");
        }}
        className="h-8 min-w-0 flex-1 rounded-md border border-line bg-surface px-2 text-xs text-content outline-none focus:border-brand-400"
      >
        {choices.flatMap((column) => [
          <option key={`${column.key}:asc`} value={`${column.key}:asc`}>
            {column.label} ↑
          </option>,
          <option key={`${column.key}:desc`} value={`${column.key}:desc`}>
            {column.label} ↓
          </option>,
        ])}
      </select>
    </label>
  );
}

/**
 * Which edge of a heading to mark, given what is being carried where.
 *
 * The side says where the column will land: against the left edge of one you
 * are dragging back past, against the right edge of one you are dragging
 * forward past. Marking the same side every time reads as "it goes here" and
 * is wrong half the time.
 */
function dropEdgeFor(
  reorder: { dragging: number | null; over: number | null },
  index: number,
): "left" | "right" | null {
  const { dragging, over } = reorder;
  if (dragging === null || over !== index || dragging === index) return null;
  return index < dragging ? "left" : "right";
}

function Th({
  children,
  className,
  width,
  onResize,
  onResetWidth,
  drag,
  dropSide,
}: {
  children: React.ReactNode;
  className?: string;
  /** Pixels. Omitted for the company column, which takes what is left. */
  width?: number;
  onResize?: (px: number) => void;
  onResetWidth?: () => void;
  /**
   * `useReorder`'s row and handle props, so the heading is its own drag
   * handle. Taken as one object and unpacked here because the hook supplies a
   * `style` of its own, and blindly spreading it would drop the width.
   */
  drag?: React.ThHTMLAttributes<HTMLTableCellElement> & {
    ref?: React.Ref<HTMLTableCellElement>;
  };
  /** Which edge the carried column would land against, while one is in flight. */
  dropSide?: "left" | "right" | null;
}) {
  const { style: dragStyle, ...dragProps } = drag ?? {};

  return (
    <th
      {...dragProps}
      style={{ ...(width === undefined ? null : { width }), ...dragStyle }}
      className={cn(
        "relative truncate border-b border-line bg-app px-3 py-2 font-medium",
        className,
      )}
    >
      {children}

      {/* A bar drawn over the edge rather than a border on it. A border would
          be two pixels of layout appearing and disappearing under the pointer,
          which shifts every heading to its right while somebody is aiming at
          one of them. */}
      {dropSide ? (
        <span
          aria-hidden
          className={cn(
            "absolute inset-y-0 z-20 w-0.5 bg-brand-500",
            dropSide === "left" ? "left-0" : "right-0",
          )}
        />
      ) : null}

      {onResize ? (
        <ColumnResizer
          width={width}
          onResize={onResize}
          onReset={onResetWidth}
        />
      ) : null}
    </th>
  );
}

/**
 * The grab handle on a column's right edge.
 *
 * The width is written straight onto the `<th>` while the pointer moves and
 * only put into React state when it is let go. Re-rendering fifty rows of a
 * dozen cells on every pointer event makes the drag lag behind the mouse, and a
 * resize that does not keep up feels broken however correct it ends up.
 *
 * Keyboard-adjustable too, because a column too narrow to read its own heading
 * is otherwise unrecoverable for anybody not using a mouse.
 */
function ColumnResizer({
  width,
  onResize,
  onReset,
}: {
  width?: number;
  onResize: (px: number) => void;
  onReset?: () => void;
}) {
  const handle = useRef<HTMLSpanElement>(null);

  const columnWidth = (): number =>
    handle.current?.closest("th")?.getBoundingClientRect().width ?? 0;

  const onPointerDown = (event: React.PointerEvent<HTMLSpanElement>): void => {
    const th = handle.current?.closest("th");
    const target = event.currentTarget;
    if (!th || event.button !== 0) return;

    /* Or the pointer picks up the header's sort button on the way past, and
       the column re-sorts every time somebody widens it. */
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = th.getBoundingClientRect().width;
    const widthAt = (clientX: number): number =>
      clampWidth(startWidth + clientX - startX);

    target.setPointerCapture(event.pointerId);

    const move = (moved: PointerEvent): void => {
      th.style.width = `${widthAt(moved.clientX)}px`;
    };

    const stop = (ended: PointerEvent): void => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", stop);
      target.removeEventListener("pointercancel", stop);
      onResize(widthAt(ended.clientX));
    };

    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", stop);
    /* A cancelled drag still commits where it got to: the column is already
       sitting at that width on screen, and snapping it back would look like
       the resize had failed. */
    target.addEventListener("pointercancel", stop);
  };

  return (
    <span
      ref={handle}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      aria-valuenow={width}
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={MAX_WIDTH}
      tabIndex={0}
      title="Drag to resize. Double-click to reset."
      onPointerDown={onPointerDown}
      onDoubleClick={() => {
        /* The width the drag wrote directly on the `<th>` has to go with it,
           or the element style keeps overriding the default we just restored. */
        const th = handle.current?.closest("th");
        if (th) th.style.width = "";
        onReset?.();
      }}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 40 : 8;
        if (event.key === "ArrowLeft") onResize(columnWidth() - step);
        else if (event.key === "ArrowRight") onResize(columnWidth() + step);
        else return;
        event.preventDefault();
      }}
      /* The heading's whole right padding, so it can be hit without aiming --
         and it has to be, because the rest of the heading now drags the column
         somewhere else, and a near miss that reorders instead of resizing
         reads as the resize being broken. Twelve pixels is exactly the padding
         and so never covers a letter. The line only appears under the pointer;
         a rule between every heading would read as a change to the table
         rather than a control. */
      className="group/resize absolute right-0 top-0 z-10 flex h-full w-3 cursor-col-resize touch-none select-none justify-end focus-visible:outline-none"
    >
      <span className="h-full w-px bg-transparent group-hover/resize:bg-brand-400 group-focus-visible/resize:bg-brand-500" />
    </span>
  );
}

/**
 * One filter in the bar, with the control its field deserves.
 *
 * A picklist gets a select and free text gets a box, because those are
 * genuinely different questions: an option is chosen from a known set and a
 * city is typed, and offering a text box for Stage invites somebody to type
 * "oppty" and get nothing.
 *
 * The remove control is on the filter rather than in a settings screen. Getting
 * rid of a filter you never use is a two-second decision made while looking at
 * it, and routing that through a preferences page is how the bar stays full of
 * things nobody wanted.
 */
function FilterControl({
  option,
  value,
  owners,
  picklists,
  onChange,
  onRemove,
}: {
  option: FilterOption;
  value: string;
  /** `value` is the count of companies they manage -- see CompanyFilters. */
  owners: { id: number; name: string; value: number }[];
  picklists: Record<string, PicklistOption[]>;
  onChange: (value: string) => void;
  onRemove: () => void;
}) {
  const options: { value: string; label: string }[] =
    option.kind === "user"
      ? [
          { value: "none", label: "Unassigned" },
          ...owners.map((owner) => ({
            value: String(owner.id),
            label: `${owner.name} (${owner.value})`,
          })),
        ]
      : option.picklist
        ? (picklists[option.picklist] ?? [])
        : /* A custom dropdown's options are its own, stored on the field and
             shared with nothing, so they arrive as bare strings. */
          (option.options ?? []).map((entry) => ({ value: entry, label: entry }));

  const isSelect = option.kind === "picklist" || option.kind === "user";

  /*
   * In or out. A leading `!` on the value is "is not" -- see `filterFor` --
   * and the switch is held here as well as read from the value, so somebody
   * can choose "is not" first and the option second, which is the order the
   * sentence "everyone not from the trade show" is said in.
   */
  const [not, setNot] = useState(value.startsWith("!"));
  useEffect(() => {
    if (value) setNot(value.startsWith("!"));
  }, [value]);
  const bare = value.startsWith("!") ? value.slice(1) : value;
  const write = (next: string, negate = not): void =>
    onChange(next ? `${negate ? "!" : ""}${next}` : "");

  return (
    <span
      className={cn(
        "group/filter relative inline-flex items-center rounded-md max-md:w-full",
        not && "ring-1 ring-sla-breached/50",
      )}
    >
      <button
        type="button"
        onClick={() => {
          setNot(!not);
          if (bare) write(bare, !not);
        }}
        aria-pressed={not}
        title={not ? "Excluding — press to include instead" : "Including — press to exclude instead"}
        className={cn(
          "mr-1 h-8 shrink-0 rounded-md px-1.5 text-[11px] font-medium max-md:w-12",
          not
            ? "bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300"
            : "text-faint hover:bg-surface-2 hover:text-content",
        )}
      >
        {not ? "is not" : "is"}
      </button>
      {isSelect ? (
        <Select
          label={option.label}
          value={bare}
          onChange={(next) => write(next)}
          options={options}
        />
      ) : (
        <TextFilter
          label={option.label}
          value={bare}
          onChange={(next) => write(next)}
        />
      )}

      {/* Only on hover or focus: a row of permanent × buttons makes the bar
          look like a list of things to delete rather than tools to use. */}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove the ${option.label} filter`}
        title={`Remove the ${option.label} filter`}
        className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full border border-line bg-surface text-[9px] text-muted hover:text-sla-breached group-hover/filter:flex group-focus-within/filter:flex"
      >
        ✕
      </button>
    </span>
  );
}

/**
 * A free-text filter that types smoothly and does not refetch per keystroke.
 *
 * The obvious version -- `value` straight from the URL, `onChange` writing it
 * straight back -- is broken in two ways that only show up once somebody types
 * at speed. `setSearchParams` is asynchronous, so each keystroke's handler
 * closes over the params as they were *before* the previous one landed, and
 * the characters race each other; typing "netxl" reliably left `f_domain=l`.
 * And every character was a URL write and a refetch.
 *
 * So the input owns what is being typed, and the URL is written once the
 * typing stops. The URL stays the source of truth for what is *applied* --
 * which is what keeps a filtered list bookmarkable.
 */
function TextFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  /* Follow the URL when it changes underneath -- the back button, or the row
     being cleared as its filter is removed -- but not on every render, which
     would fight the person typing. */
  useEffect(() => setDraft(value), [value]);

  useEffect(() => {
    if (draft === value) return;

    const timer = window.setTimeout(() => onChange(draft), 300);
    return () => window.clearTimeout(timer);
    /* `onChange` is rebuilt every render by the parent, so it is deliberately
       not a dependency: including it restarts the timer on every keystroke's
       re-render and the filter never applies at all. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, value]);

  return (
    <input
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      placeholder={`${label}…`}
      aria-label={`Filter by ${label}`}
      className="h-8 w-36 rounded-md border border-line bg-surface px-2 text-xs text-content outline-none focus:border-brand-400 max-md:w-auto max-md:min-w-0 max-md:flex-1"
    />
  );
}

/**
 * Add a filter, or put the bar back how it was.
 *
 * A native select used as a menu: it is one click, keyboard-navigable for
 * free, and does not need a popover with outside-click handling for what is a
 * list of field names.
 */
/**
 * Which columns this person sees.
 *
 * A checklist and not a pair of add/remove menus: the question somebody opens
 * this with is "what could be on here", and the answer is one list where what
 * is already on is ticked. A custom field a manager defined last week is in it
 * the moment it exists -- `available` is built from the field catalogue rather
 * than a hardcoded array, which is most of the point of letting fields be
 * defined at all.
 *
 * Ticking appends rather than restoring the field's catalogue position. A
 * column you just asked for should turn up where you are looking -- the end,
 * next to the menu you opened -- and the heading can be dragged from there.
 *
 * The last column cannot be unticked. A table of no columns is a page of blank
 * rows with nothing on it to put one back.
 */
function EditColumns({
  available,
  columns,
  onChange,
  onFollowTeam,
  followLabel,
}: {
  available: { key: string; label: string }[];
  columns: string[];
  onChange: (columns: string[]) => void;
  /** Absent while this person has no layer of their own to give up. */
  onFollowTeam?: () => void;
  /** Whose layout following again would mean, named so the offer is concrete. */
  followLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const shown = new Set(columns);

  const toggle = (key: string): void => {
    if (!shown.has(key)) {
      onChange([...columns, key]);
      return;
    }
    if (columns.length === 1) return;
    onChange(columns.filter((entry) => entry !== key));
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      label="Choose the columns on this list"
      className="max-h-80 w-60 overflow-y-auto p-1"
      trigger={
        <span className="flex h-8 items-center rounded-md border border-dashed border-line px-2 text-xs text-muted hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400">
          Columns
        </span>
      }
    >
      {available.map((column) => {
        const on = shown.has(column.key);
        const last = on && columns.length === 1;

        return (
          <button
            key={column.key}
            type="button"
            disabled={last}
            title={last ? "The list needs at least one column." : undefined}
            onClick={() => toggle(column.key)}
            className={cn(
              "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-surface-2",
              on ? "text-content" : "text-muted",
              last && "opacity-50",
            )}
          >
            <span aria-hidden className={on ? "text-brand-600" : "text-faint"}>
              {on ? "\u2713" : "\u00a0"}
            </span>
            {column.label}
          </button>
        );
      })}

      {/* Only once there is something to give up. Offering it to somebody who
          is already following their team would be a button that does nothing
          and reads as though it might. */}
      {onFollowTeam ? (
        <button
          type="button"
          onClick={() => {
            onFollowTeam();
            setOpen(false);
          }}
          className="mt-1 w-full rounded border-t border-line px-2 py-1.5 text-left text-[11px] text-muted hover:bg-surface-2"
        >
          Follow {followLabel} columns again
        </button>
      ) : null}
    </Popover>
  );
}

function AddFilter({
  available,
  onAdd,
  onReset,
}: {
  available: FilterOption[];
  onAdd: (key: string) => void;
  onReset?: () => void;
}) {
  return (
    <span className="flex items-center gap-1">
      {available.length > 0 ? (
        <select
          value=""
          aria-label="Add a filter"
          onChange={(event) => {
            if (event.target.value) onAdd(event.target.value);
          }}
          className="h-8 rounded-md border border-dashed border-line bg-transparent px-2 text-xs text-muted outline-none hover:border-brand-300 focus:border-brand-400"
        >
          <option value="">+ Filter</option>
          {available.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      ) : null}

      {onReset ? (
        <button
          type="button"
          onClick={onReset}
          title="Put the filter bar back to its defaults"
          className="text-[11px] text-faint hover:text-brand-600 dark:hover:text-brand-400"
        >
          Reset
        </button>
      ) : null}
    </span>
  );
}

function Select({
  label: name,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      aria-label={name}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        "h-8 rounded-md border border-line bg-surface px-2 text-xs outline-none focus:border-brand-400 max-md:min-w-0 max-md:flex-1",
        value ? "text-content" : "text-faint",
      )}
    >
      <option value="">{name}: any</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
